import {
  apiAgentsRetrieve,
  apiApprovalsRetrieve,
  apiObservabilityRunsFeedbackUpdate,
  apiObservabilityThreadsRatingsRetrieve,
  apiRunsCancelCreate,
  apiRunsStepsList,
  apiThreadsDestroy,
  apiThreadsList,
  apiThreadsMessagesRetrieve,
  apiThreadsRenamePartialUpdate
} from 'src/api/squad/sdk.gen'
import type {
  Agent,
  Approval,
  ChatFile,
  PaginatedThreadList,
  ThreadActiveRun
} from 'src/api/squad/types.gen'

const DEFAULT_PAGE_SIZE = 15

const appAuth = (appToken: string) => ({
  headers: { 'Saas-App-Token': appToken }
})

/** The one agent this workspace chats with — its name and its avatar. */
export const getAgent = async (
  appToken: string,
  agentId: string
): Promise<Agent | undefined> => {
  const { data } = await apiAgentsRetrieve({
    ...appAuth(appToken),
    path: { id: agentId }
  })

  return data
}

export type ThreadMessage = {
  role: 'user' | 'assistant'
  text: string
  created_at?: string
  run_id?: string
  status?: string
  working_steps?: { kind: string; data?: Record<string, unknown> }[]
  files?: ChatFile[]
}

export type ThreadMessages = {
  messages: ThreadMessage[]
  active_run?: ThreadActiveRun | string | null
}

export type RunRating = { trace_id: string; up: boolean | null }

export type ThreadRatings = {
  enabled?: boolean
  ratings: Record<string, RunRating>
}

export const listThreads = async (
  appToken: string,
  agentId: string,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  search = '',
  userSsoId?: string
): Promise<PaginatedThreadList> => {
  const { data } = await apiThreadsList({
    ...appAuth(appToken),
    query: {
      agent: agentId,
      page,
      page_size: pageSize,
      ...(search ? { search } : {}),
      ...(userSsoId ? { user: userSsoId } : {})
    },
    throwOnError: true
  })

  return {
    count: data.count ?? 0,
    next: data.next ?? null,
    previous: data.previous ?? null,
    results: data.results ?? []
  }
}

export const getThreadMessages = async (
  appToken: string,
  threadId: string
): Promise<ThreadMessages> => {
  const { data } = await apiThreadsMessagesRetrieve({
    ...appAuth(appToken),
    path: { id: threadId },
    throwOnError: true
  })

  return data as unknown as ThreadMessages
}

export const getApproval = async (
  appToken: string,
  approvalId: string
): Promise<Approval> => {
  const { data } = await apiApprovalsRetrieve({
    ...appAuth(appToken),
    path: { id: approvalId },
    throwOnError: true
  })

  return data
}

export const renameThread = async (
  appToken: string,
  threadId: string,
  title: string
): Promise<void> => {
  await apiThreadsRenamePartialUpdate({
    ...appAuth(appToken),
    path: { id: threadId },
    body: { title },
    throwOnError: true
  })
}

export const deleteThread = async (
  appToken: string,
  threadId: string
): Promise<void> => {
  await apiThreadsDestroy({
    ...appAuth(appToken),
    path: { id: threadId },
    throwOnError: true
  })
}

export const getRunStepCount = async (
  appToken: string,
  runId: string
): Promise<number> => {
  const { data } = await apiRunsStepsList({
    ...appAuth(appToken),
    path: { run_id: runId },
    throwOnError: true
  })

  return data?.length ?? 0
}

export const cancelRun = async (
  appToken: string,
  runId: string
): Promise<void> => {
  await apiRunsCancelCreate({
    ...appAuth(appToken),
    path: { id: runId },
    throwOnError: true
  })
}

export const getThreadRatings = async (
  appToken: string,
  threadId: string
): Promise<ThreadRatings> => {
  const { data } = await apiObservabilityThreadsRatingsRetrieve({
    ...appAuth(appToken),
    path: { thread_id: threadId },
    throwOnError: true
  })

  return data as ThreadRatings
}

export const rateRun = async (
  appToken: string,
  runId: string,
  up: boolean
): Promise<void> => {
  await apiObservabilityRunsFeedbackUpdate({
    ...appAuth(appToken),
    path: { run_id: runId },
    body: { up },
    throwOnError: true
  })
}
